import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Download, X, Share, Plus } from "lucide-react";

const DISMISS_KEY = "rh_install_dismissed";

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

const isIOS = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;

const isMobile = () =>
  /android|iphone|ipad|ipod/i.test(window.navigator.userAgent) ||
  window.matchMedia("(max-width: 900px)").matches;

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || localStorage.getItem(DISMISS_KEY) === "1" || !isMobile()) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    let iosTimer;
    if (isIOS()) {
      iosTimer = setTimeout(() => setShow(true), 2500);
    }

    const onInstalled = () => {
      setShow(false);
      localStorage.setItem(DISMISS_KEY, "1");
    };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => {
    setShow(false);
    setIosHint(false);
    localStorage.setItem(DISMISS_KEY, "1");
  };

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      setDeferred(null);
      setShow(false);
      if (outcome !== "accepted") localStorage.setItem(DISMISS_KEY, "1");
    } else if (isIOS()) {
      setIosHint(true);
    }
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ type: "spring", stiffness: 320, damping: 30 }}
          className="fixed bottom-4 inset-x-4 z-[60] lg:hidden"
          data-testid="install-prompt"
        >
          <div className="mx-auto max-w-md rounded-2xl bg-slate-900 text-white shadow-2xl border border-white/10 overflow-hidden">
            <div className="flex items-center gap-3 p-4">
              <div className="w-11 h-11 rounded-xl overflow-hidden shrink-0 ring-1 ring-white/15">
                <img src="/icon-192.png" alt="RouteHaul" className="w-full h-full object-cover" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-heading font-bold text-sm leading-tight">Add RouteHaul to your home screen</p>
                <p className="text-xs text-slate-400 mt-0.5">Open it like an app — one tap, no browser bar.</p>
              </div>
              <button
                onClick={dismiss}
                data-testid="install-dismiss"
                aria-label="Dismiss"
                className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {iosHint ? (
              <div className="px-4 pb-4 -mt-1" data-testid="install-ios-hint">
                <div className="rounded-xl bg-white/5 p-3 text-xs text-slate-300 leading-relaxed">
                  In Safari, tap the <Share className="inline w-3.5 h-3.5 mx-0.5 -mt-0.5" /> <span className="font-semibold text-white">Share</span> button below,
                  then choose <span className="font-semibold text-white">Add to Home Screen</span> <Plus className="inline w-3.5 h-3.5 mx-0.5 -mt-0.5" />.
                </div>
              </div>
            ) : (
              <div className="px-4 pb-4 -mt-1">
                <button
                  onClick={install}
                  data-testid="install-button"
                  className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-full bg-brand hover:bg-brand-hover transition-colors text-white text-sm font-bold"
                >
                  <Download className="w-4 h-4" /> {isIOS() ? "Show me how" : "Install app"}
                </button>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
